import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import type { ParagraphDto } from '../../api/queries/books';
import { useParagraphImages } from '../../api/queries/media';
import { useParagraphAudio } from '../../api/queries/audio';
import { useSelectionStore } from '../../stores/selection.store';
import { usePlaybackStore } from '../../stores/playback.store';
import { AppImage } from '../common/AppImage';
import { AudioWaveform } from './AudioWaveform';

interface SceneCompositionViewProps {
  paragraphs: ParagraphDto[];
  bookId: string;
  onGenerateImage?: () => void;
  onGenerateAudio?: () => void;
}

export function SceneCompositionView({
  paragraphs,
  bookId: _bookId,
  onGenerateImage,
  onGenerateAudio,
}: SceneCompositionViewProps) {
  const selectedId = useSelectionStore((s) => s.paragraphId);
  const select = useSelectionStore((s) => s.select);

  if (paragraphs.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 4, textAlign: 'center' }}
      >
        No paragraphs in this chapter.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Header row */}
      <Stack
        direction="row"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
          mb: 1,
          px: 1,
        }}
      >
        <Typography variant="caption" sx={{ flex: 2, fontWeight: 600 }}>
          Text
        </Typography>
        <Typography
          variant="caption"
          sx={{ flex: 1, fontWeight: 600, textAlign: 'center' }}
        >
          Image
        </Typography>
        <Typography
          variant="caption"
          sx={{ flex: 1, fontWeight: 600, textAlign: 'center' }}
        >
          Audio
        </Typography>
        <Typography
          variant="caption"
          sx={{ width: 40, fontWeight: 600, textAlign: 'center' }}
        >
          Status
        </Typography>
      </Stack>

      {/* Paragraph rows */}
      {paragraphs.map((p) => (
        <SceneRow
          key={p.id}
          paragraph={p}
          isSelected={p.id === selectedId}
          onSelect={() => select(p.id)}
          onGenerateImage={onGenerateImage}
          onGenerateAudio={onGenerateAudio}
        />
      ))}
    </Box>
  );
}

interface SceneRowProps {
  paragraph: ParagraphDto;
  isSelected: boolean;
  onSelect: () => void;
  onGenerateImage?: () => void;
  onGenerateAudio?: () => void;
}

function SceneRow({
  paragraph,
  isSelected,
  onSelect,
  onGenerateImage,
  onGenerateAudio,
}: SceneRowProps) {
  const { data: images } = useParagraphImages(paragraph.id);
  const { data: audioFiles } = useParagraphAudio(paragraph.id);
  const play = usePlaybackStore((s) => s.play);

  const hasImage = images && images.length > 0;
  const hasAudio = audioFiles && audioFiles.length > 0;
  const latestAudio = hasAudio ? audioFiles[audioFiles.length - 1] : null;

  const statusColor =
    hasImage && hasAudio
      ? 'success'
      : hasImage || hasAudio
        ? 'warning'
        : 'error';
  const StatusIcon =
    statusColor === 'success'
      ? CheckCircleIcon
      : statusColor === 'warning'
        ? WarningAmberIcon
        : ErrorOutlineIcon;

  const textPreview = paragraph.contentHtml.replace(/<[^>]+>/g, '').trim();

  return (
    <Stack
      direction="row"
      onClick={onSelect}
      sx={{
        cursor: 'pointer',
        alignItems: 'stretch',
        minHeight: 80,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'primary.main' : 'divider',
        mb: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Text column */}
      <Box sx={{ flex: 2, pr: 1, overflow: 'hidden' }}>
        <Typography
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {textPreview || 'Empty paragraph'}
        </Typography>
      </Box>

      {/* Image column */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {hasImage ? (
          <AppImage
            src={images[0]!.thumbnailUrl ?? images[0]!.url}
            width={80}
            height={60}
            objectFit="cover"
            sx={{ borderRadius: 0.5 }}
          />
        ) : (
          <Tooltip title="Generate image">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onGenerateImage?.();
              }}
              sx={{ color: 'text.disabled' }}
            >
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Audio column */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
        }}
      >
        {hasAudio ? (
          <>
            <AudioWaveform
              durationMs={latestAudio?.durationMs}
              width={80}
              height={24}
            />
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                play(paragraph.id);
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        ) : (
          <Tooltip title="Generate audio">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onGenerateAudio?.();
              }}
              sx={{ color: 'text.disabled' }}
            >
              <AudiotrackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Status column */}
      <Box
        sx={{
          width: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Tooltip
          title={
            statusColor === 'success'
              ? 'Complete'
              : statusColor === 'warning'
                ? 'Partial'
                : 'Missing assets'
          }
        >
          <StatusIcon color={statusColor} sx={{ fontSize: 18 }} />
        </Tooltip>
      </Box>
    </Stack>
  );
}
